import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Text from 'Components/ComponentLibrary/Atoms/Text';
import fr from 'facet-rest';
import numeral from 'numeral';
import CollapsibleDisclaimer from 'Components/ComponentLibrary/Components/Uncategorized/CollapsibleDisclaimer';
import AreaChart from 'Components/ComponentLibrary/Components/Uncategorized/AreaChart';

const useStyles = buildUseStyles('HoldingsSectionContent', (theme) => ({
  root: {},
  title: {},
  chartContainer: {
    borderBottom: `1px dotted ${theme.palette.facet.mediumGray}`,
    marginBottom: theme.spacing(2),
  },
  holdingsSection: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    '&:not(:last-child)': {
      marginBottom: theme.spacing(2),
    },
  },
  holdingsSectionLine: {
    marginBottom: theme.spacing(2),
  },
  line: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  holdingsName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  disclaimer: {
    marginTop: theme.spacing(2),
  },
}));

const InvestmentReturnsSectionContent = ({
  className,
  modelPerformanceData,
  modelPerformanceChartData,
}) => {
  const [modelPerformanceDataResource] = modelPerformanceData;

  const data = modelPerformanceChartData.resources().list().map(resource => {
    const attributes = resource.attributes();
    return { ...attributes, value: Number(attributes.value) };
  });
  const theme = useTheme();
  const classes = useStyles(theme);

  const formatNumber = (value) => {
    return numeral(value).format('0a');
  };

  if (!modelPerformanceDataResource) return null;

  return (
    <Box
      className={classNames(classes.root, className)}
    >
      <AreaChart
        className={classes.chartContainer}
        data={data}
        height={150}
        xDataKey={'date'}
        yTicks={[100000, 300000]}
        xTicks={['04', '08', '12', '16', '20', '24']}
        yTickFormatFn={formatNumber}
        margin={{
          top: 10, right: 0, left: -15, bottom: 10,
        }}
      />
      <Box className={classes.line}>
        <Text color={'primaryGray'} className={classes.startingBalance} text={'Starting Balance'} />
        <Text color={'primaryGray'} className={classes.startingBalance} text={modelPerformanceDataResource.attr('startingBalance')} />
      </Box>
      <Box className={classes.line}>
        <Text color={'primaryGray'} className={classes.endingBalance} text={'Ending Balance'} />
        <Text color={'primaryGray'} className={classes.endingBalance} text={modelPerformanceDataResource.attr('endingBalance')} />
      </Box>
      <Box className={classes.line}>
        <Text color={'primaryGray'} className={classes.totalReturn} text={'Total Return ($)'} />
        <Text color={'primaryGray'} className={classes.totalReturn} text={modelPerformanceDataResource.attr('totalReturn')} />
      </Box>
      <Box className={classes.line}>
        <Text color={'primaryGray'} className={classes.totalReturnPercent} text={'Total Return (%)'} />
        <Text color={'primaryGray'} className={classes.totalReturnPercent} text={modelPerformanceDataResource.attr('totalReturnPercent')} />
      </Box>
      <Box className={classes.line}>
        <Text color={'primaryGray'} className={classes.averageAnnualReturn} text={'Average Annual Return'} />
        <Text color={'primaryGray'} className={classes.averageAnnualReturn} text={modelPerformanceDataResource.attr('averageAnnualReturn')} />
      </Box>
      <CollapsibleDisclaimer
        className={classes.disclaimer}
        disclaimerTitle={'Hypothetical Investment Disclaimer'}
        disclaimerText={modelPerformanceDataResource.attr('investmentReturnsDisclaimer')}
        disclaimerReadMore={modelPerformanceDataResource.attr('investmentReturnsDisclaimerReadMore')}
      />
    </Box>
  );
};

InvestmentReturnsSectionContent.defaultProps = {
  className: '',
};

InvestmentReturnsSectionContent.propTypes = {
  className: PropTypes.string,
  modelPerformanceData: PropTypes.instanceOf(fr.Collection).isRequired,
  modelPerformanceChartData: PropTypes.instanceOf(fr.Collection).isRequired,
};

export default InvestmentReturnsSectionContent;
